import { Role } from 'auth';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { useRegisterPermissionsQuery } from '@/modules/common/queries/permissions.ts';
import HolderSearch from '@/modules/issuer-dashboard/components/HolderSearch.tsx';
import IssuedCapitalChart from '@/modules/issuer-dashboard/components/IssuedCapitalChart.tsx';
import TopHoldersChart from '@/modules/issuer-dashboard/components/TopHoldersChart.tsx';
import EditRequestsTab from '@/modules/issuer-dashboard/components/holder-create/EditRequestsTab.tsx';
import CommonReports from '@/modules/issuer-dashboard/components/holders/CommonReports.tsx';
import CommunicationPreference from '@/modules/issuer-dashboard/components/holders/CommunicationPreference.tsx';
import PaymentElection from '@/modules/issuer-dashboard/components/holders/PaymentElection.tsx';
import { HeaderHolders } from '@/modules/issuer-dashboard/pages/IssuerHoldersNew.tsx';
import { InfoCard, PageLayout, TabItem, Tabs } from 'ui';

export enum HoldersTabNames {
  Overview = 'Overview',
  EditRequests = 'Edit requests',
}

export const HOLDERS_TAB_NAME = 'tab-holders';

const IssuerHolders = () => {
  const [params] = useSearchParams();
  const { data: dataRegisterPermissions } = useRegisterPermissionsQuery();
  const paramTab = params.get(HOLDERS_TAB_NAME);
  const isValidTab =
    paramTab && Object.values(HoldersTabNames).includes(paramTab as HoldersTabNames);
  const [activeTab, setActiveTab] = useState<HoldersTabNames>(
    isValidTab ? (paramTab as HoldersTabNames) : HoldersTabNames.Overview,
  );

  useEffect(() => {
    const isValidTab =
      paramTab && Object.values(HoldersTabNames).includes(paramTab as HoldersTabNames);
    setActiveTab(isValidTab ? (paramTab as HoldersTabNames) : HoldersTabNames.Overview);
  }, [paramTab]);

  const tabItems = useMemo<TabItem<HoldersTabNames>[]>(() => {
    return [
      {
        name: HoldersTabNames.Overview,
        container: <OverviewTab />,
      },
      {
        name: HoldersTabNames.EditRequests,
        container: <EditRequestsTab />,
        disabled: !dataRegisterPermissions?.holder_details_permission,
        disabledReason: 'This feature is not available for this issuer',
      },
    ];
  }, [dataRegisterPermissions?.holder_details_permission]);

  return (
    <PageLayout
      title="Holders"
      headerAction={<HeaderHolders />}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabNameParam={HOLDERS_TAB_NAME}
      tabs={tabItems}
    >
      <Tabs items={tabItems} activeTab={activeTab} />
    </PageLayout>
  );
};

const OverviewTab = () => {
  return (
    <div className="flex flex-col gap-6">
      <InfoCard title="Find holder by name or ID">
        <HolderSearch />
      </InfoCard>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <CommonReports />
        <TopHoldersChart title="Top holders" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="flex flex-col gap-6">
          <CommunicationPreference />
          <PaymentElection />
        </div>
        <IssuedCapitalChart />
      </div>
    </div>
  );
};

export default withPrivateAccess(IssuerHolders, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
